import imageService from '@/core/services/imageService';
import Vue from 'vue';

const state = {
  images: [],
  loading: false,
  currentImage: null,
  uploadProgress: {},
  imageUsages: {},
};

const mutations = {
  SET_IMAGES(state, images) {
    state.images = images;
  },
  ADD_IMAGE(state, image) {
    state.images.unshift(image);
  },
  SET_CURRENT_IMAGE(state, image) {
    state.currentImage = image;
  },
  REMOVE_IMAGE(state, imageId) {
    state.images = state.images.filter((image) => image.id !== imageId);
  },
  SET_LOADING(state, status) {
    state.loading = status;
  },
  SET_UPLOAD_PROGRESS(state, { id, progress }) {
    Vue.set(state.uploadProgress, id, progress);
  },
  CLEAR_UPLOAD_PROGRESS(state, id) {
    Vue.delete(state.uploadProgress, id);
  },
  SET_IMAGE_USAGES(state, { imageId, usages }) {
    Vue.set(state.imageUsages, imageId, usages);
  },
};

const actions = {
  async fetchImages({ commit }) {
    commit('SET_LOADING', true);
    try {
      const response = await imageService.getAllImages();
      commit('SET_IMAGES', response.data.images || []);
      return response.data.images;
    } catch (error) {
      console.error('Error fetching images:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async uploadImage({ commit }, file) {
    commit('SET_LOADING', true);
    try {
      const response = await imageService.uploadImage(file);
      if (response.data.success) {
        commit('ADD_IMAGE', response.data.image);
      }
      return response.data;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getImageById({ commit }, imageId) {
    commit('SET_LOADING', true);
    try {
      const response = await imageService.getImageById(imageId);
      commit('SET_CURRENT_IMAGE', response.data.image);
      return response.data.image;
    } catch (error) {
      console.error(`Error fetching image ${imageId}:`, error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteImage({ commit }, imageId) {
    commit('SET_LOADING', true);
    try {
      const response = await imageService.deleteImage(imageId);
      if (response.data.success) {
        commit('REMOVE_IMAGE', imageId);
      }
      return response.data;
    } catch (error) {
      console.error(`Error deleting image ${imageId}:`, error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  setUploadProgress({ commit }, { id, progress }) {
    commit('SET_UPLOAD_PROGRESS', { id, progress });
  },

  clearUploadProgress({ commit }, id) {
    commit('CLEAR_UPLOAD_PROGRESS', id);
  },

  async getImageUsages({ commit }, imageId) {
    try {
      const response = await imageService.getImageUsages(imageId);
      commit('SET_IMAGE_USAGES', {
        imageId,
        usages: response.data.usages || [],
      });
      return response.data.usages;
    } catch (error) {
      console.error(`Error fetching image usages ${imageId}:`, error);
      throw error;
    }
  },
};

const getters = {
  allImages: (state) => state.images,
  imageById: (state) => (id) => state.images.find((image) => image.id === id),
  isLoading: (state) => state.loading,
  currentImage: (state) => state.currentImage,
  uploadProgress: (state) => (id) => state.uploadProgress[id] || 0,
  imageUsagesById: (state) => (imageId) => state.imageUsages[imageId] || [],
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
